/**
 * AISEN — Trava de quantidade para rolos TRC no carrinho.
 * Só afeta linhas com a propriedade oculta _rolo === 'trc' (produtos AISEN FLEX CUSTOM — TRC).
 * Se a quantidade sair da lista de rolos permitidos, corrige para o rolo válido mais próximo
 * (via /cart/change.js) e reaproveita o morphSection nativo do Horizon para atualizar a UI.
 * Não altera nenhum outro produto do carrinho.
 */
import { morphSection } from '@theme/section-renderer';
import { debounce } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

const ALLOWED = [25, 50, 100, 200, 300, 400, 500];

const nearest = (n) =>
  ALLOWED.reduce(
    (best, a) => (Math.abs(a - n) < Math.abs(best - n) || (Math.abs(a - n) === Math.abs(best - n) && a > best) ? a : best),
    ALLOWED[0]
  );

let busy = false;

async function guard() {
  if (busy) return;
  let cart;
  try {
    cart = await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();
  } catch (e) {
    return;
  }
  const bad = [];
  (cart.items || []).forEach((it) => {
    const props = it.properties || {};
    if (props._rolo === 'trc' && ALLOWED.indexOf(it.quantity) === -1) {
      bad.push({ key: it.key, to: nearest(it.quantity) });
    }
  });
  if (!bad.length) return;

  busy = true;
  const carts = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'));
  const sids = Array.from(new Set(carts.map((c) => c.dataset.sectionId).filter(Boolean)));
  let last;
  try {
    for (const b of bad) {
      last = b.to;
      const res = await (
        await fetch(Theme.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: b.key,
            quantity: b.to,
            sections: sids.join(','),
            sections_url: window.location.pathname,
          }),
        })
      ).json();

      sids.forEach((sid) => {
        const html = res.sections && res.sections[sid];
        if (!html) return;
        const isDrawer = document.querySelector(`cart-items-component[data-section-id="${sid}"]`)?.dataset.drawer !== undefined;
        try {
          morphSection(sid, html, { mode: isDrawer ? 'hydration' : 'full' });
        } catch (e) {
          const wrapper = document.getElementById(`shopify-section-${sid}`);
          const parsed = new DOMParser().parseFromString(html, 'text/html').getElementById(`shopify-section-${sid}`);
          if (wrapper && parsed) wrapper.innerHTML = parsed.innerHTML;
        }
      });
    }
    if (last != null) notify(`Este produto é vendido em rolos. Ajustamos para o rolo de ${last} m.`);
  } finally {
    busy = false;
  }
}

const runGuard = debounce(guard, 300);

// Após cada atualização de linha do carrinho (promise resolve quando o Horizon termina o morph).
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const p = event && event.promise;
  if (p && typeof p.then === 'function') {
    p.then(() => runGuard()).catch(() => {});
  } else {
    runGuard();
  }
});

// Carga inicial (ex.: carrinho persistido com quantidade inválida vinda de outra origem).
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => setTimeout(guard, 400));
} else {
  setTimeout(guard, 400);
}

// ---- aviso acessível ----
let toastEl = null;
function notify(msg) {
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    toastEl.style.cssText =
      'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:9999;background:#0D1B2A;color:#fff;font:600 13px/1.4 Montserrat,system-ui,sans-serif;padding:12px 18px;border-radius:10px;box-shadow:0 8px 30px rgba(0,0,0,.25);max-width:90vw;text-align:center;opacity:0;transition:opacity .2s;';
    document.body.appendChild(toastEl);
  }
  toastEl.textContent = msg;
  toastEl.style.opacity = '1';
  clearTimeout(toastEl._t);
  toastEl._t = setTimeout(() => {
    if (toastEl) toastEl.style.opacity = '0';
  }, 4000);
}
